<!-- 弹幕层：负责轨道分配、滚动动画与播放进度同步 -->
<template>
  <div class="danmaku-layer" :style="{ opacity: opacity, '--dm-width': layerWidth + 'px' }">
    <!-- 滚动弹幕 -->
    <div
      v-for="item in activeScroll"
      :key="'s' + item.key"
      class="dm dm-scroll"
      :style="scrollStyle(item)"
    >
      <span v-if="item.noble_level > 0" class="dm-noble" :class="'dm-noble-' + item.noble_level">V{{ item.noble_level }}</span>{{ item.content }}
    </div>

    <!-- 顶部固定弹幕 -->
    <div
      v-for="item in activeTop"
      :key="'t' + item.key"
      class="dm dm-fixed dm-top"
      :style="fixedStyle(item)"
    >
      <span v-if="item.noble_level > 0" class="dm-noble" :class="'dm-noble-' + item.noble_level">V{{ item.noble_level }}</span>{{ item.content }}
    </div>

    <!-- 底部固定弹幕 -->
    <div
      v-for="item in activeBottom"
      :key="'b' + item.key"
      class="dm dm-fixed dm-bottom"
      :style="fixedStyle(item)"
    >
      <span v-if="item.noble_level > 0" class="dm-noble" :class="'dm-noble-' + item.noble_level">V{{ item.noble_level }}</span>{{ item.content }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'DanmakuLayer',
  props: {
    // 全量弹幕数据（含 video_time 时间点）
    list: { type: Array, default: () => [] },
    // 当前播放时间（秒），由父组件的 timeupdate 驱动
    currentTime: { type: Number, default: 0 },
    // 是否显示
    visible: { type: Boolean, default: true },
    // 透明度 0.1 - 1
    opacity: { type: Number, default: 1 },
    // 滚动时长（秒），越小越快
    duration: { type: Number, default: 10 },
    // 字号缩放 0.8 / 1 / 1.25
    scale: { type: Number, default: 1 },
    // 滚动轨道数
    trackCount: { type: Number, default: 8 }
  },
  data() {
    return {
      activeScroll: [],
      activeBottom: [],
      activeTop: [],
      // 已消费过的弹幕 id，避免 seek 回退后重复飘
      consumed: new Set(),
      // 每条轨道下次可用时间戳（ms）
      trackFreeAt: [],
      keySeed: 0,
      lastTime: 0,
      // 弹幕层实际宽度，用于计算滚动位移
      layerWidth: 800,
      resizeObserver: null
    };
  },
  watch: {
    currentTime(now, before) {
      if (!this.visible) return;
      // 拖动进度条（跨度大于 2 秒）时清屏重置
      if (Math.abs(now - before) > 2) {
        this.reset();
        this.lastTime = now;
        return;
      }
      this.emitRange(this.lastTime, now);
      this.lastTime = now;
    },
    visible(val) {
      if (!val) this.reset();
    },
    trackCount() {
      this.trackFreeAt = new Array(this.trackCount).fill(0);
    }
  },
  created() {
    this.trackFreeAt = new Array(this.trackCount).fill(0);
  },
  mounted() {
    this.measure();
    // 容器尺寸变化（全屏/窗口缩放）时重新测量，保证滚动距离正确
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.$el);
    } else {
      window.addEventListener('resize', this.measure);
    }
  },
  beforeUnmount() {
    this.reset();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    } else {
      window.removeEventListener('resize', this.measure);
    }
  },
  methods: {
    measure() {
      if (this.$el && this.$el.clientWidth) {
        this.layerWidth = this.$el.clientWidth;
      }
    },

    // 清空屏幕上的弹幕并重置消费记录
    reset() {
      this.activeScroll = [];
      this.activeTop = [];
      this.activeBottom = [];
      this.consumed = new Set();
      this.trackFreeAt = new Array(this.trackCount).fill(0);
    },

    // 命中 (from, to] 区间的弹幕并投放
    emitRange(from, to) {
      if (to <= from) return;
      for (let i = 0; i < this.list.length; i++) {
        const item = this.list[i];
        const t = Number(item.video_time) || 0;
        if (t > from && t <= to && !this.consumed.has(item.id)) {
          this.consumed.add(item.id);
          this.push(item);
        }
      }
    },

    // 构造弹幕行
    buildRow(item, isSelf) {
      const key = ++this.keySeed;
      return {
        key,
        id: item.id,
        content: item.content,
        color: item.color || '#ffffff',
        fontsize: item.fontsize || 24,
        position: item.position || 'scroll',
        is_noble: !!item.is_noble,
        noble_level: (item.noble_level !== undefined ? item.noble_level : (item.is_noble ? 1 : 0)) || 0,
        self: isSelf,
        // 固定弹幕的轨道索引（用于多行显示，避免依赖 indexOf）
        fixedTrack: 0
      };
    },

    // 为固定弹幕分配轨道索引（寻找空闲轨道）
    pickFixedTrack(arr) {
      const used = new Set(arr.map(v => v.fixedTrack));
      for (let i = 0; i < 4; i++) {
        if (!used.has(i)) return i;
      }
      return 0;
    },

    // 立即投放一条（自己刚发的弹幕，无需等时间轴）
    pushNow(item, isSelf = true) {
      if (item.id) this.consumed.add(item.id);
      const position = item.position || 'scroll';
      const row = this.buildRow(item, isSelf);

      if (position === 'top') {
        row.fixedTrack = this.pickFixedTrack(this.activeTop);
        this.activeTop.push(row);
        if (this.activeTop.length > 4) this.activeTop.shift();
        setTimeout(() => {
          this.activeTop = this.activeTop.filter(v => v.key !== row.key);
        }, 4000);
        return;
      }
      if (position === 'bottom') {
        row.fixedTrack = this.pickFixedTrack(this.activeBottom);
        this.activeBottom.push(row);
        if (this.activeBottom.length > 4) this.activeBottom.shift();
        setTimeout(() => {
          this.activeBottom = this.activeBottom.filter(v => v.key !== row.key);
        }, 4000);
        return;
      }
      // 滚动弹幕
      row.track = this.pickTrack(row.content.length);
      this.activeScroll.push(row);
      if (this.activeScroll.length > 120) this.activeScroll.shift();
      setTimeout(() => {
        this.activeScroll = this.activeScroll.filter(v => v.key !== row.key);
      }, this.duration * 1000 + 200);
    },

    push(item, isSelf = false) {
      const position = item.position || 'scroll';
      const row = this.buildRow(item, isSelf);

      if (position === 'top') {
        row.fixedTrack = this.pickFixedTrack(this.activeTop);
        this.activeTop.push(row);
        if (this.activeTop.length > 4) this.activeTop.shift();
        setTimeout(() => {
          this.activeTop = this.activeTop.filter(v => v.key !== row.key);
        }, 4000);
        return;
      }

      if (position === 'bottom') {
        row.fixedTrack = this.pickFixedTrack(this.activeBottom);
        this.activeBottom.push(row);
        if (this.activeBottom.length > 4) this.activeBottom.shift();
        setTimeout(() => {
          this.activeBottom = this.activeBottom.filter(v => v.key !== row.key);
        }, 4000);
        return;
      }

      // 滚动弹幕：挑一条空闲轨道，避免重叠
      row.track = this.pickTrack(row.content.length);
      this.activeScroll.push(row);
      // 上限保护，防止长时间累积
      if (this.activeScroll.length > 120) this.activeScroll.shift();

      setTimeout(() => {
        this.activeScroll = this.activeScroll.filter(v => v.key !== row.key);
      }, this.duration * 1000 + 200);
    },

    // 轨道分配：选择当前最早空闲的一条，占用时长按文字长度估算
    pickTrack(len) {
      const now = Date.now();
      let best = 0;
      let bestFree = Infinity;
      for (let i = 0; i < this.trackCount; i++) {
        const free = this.trackFreeAt[i] || 0;
        if (free <= now) {
          best = i;
          bestFree = free;
          break;
        }
        if (free < bestFree) {
          bestFree = free;
          best = i;
        }
      }
      // 占用时间 = 文字完全离开右边界所需时间的估算
      const textWidth = len * 16 * this.scale;
      const total = this.layerWidth + textWidth;
      const speed = total / this.duration; // px/s
      const occupy = Math.max(900, (textWidth / speed) * 1000 + 600);
      this.trackFreeAt[best] = Math.max(now, bestFree) + occupy;
      return best;
    },

    scrollStyle(item) {
      const size = Math.round(item.fontsize * this.scale);
      return {
        top: item.track * (size + 8) + 6 + 'px',
        color: item.color,
        fontSize: size + 'px',
        animationDuration: this.duration + 's',
        textShadow: item.self
          ? '0 0 6px rgba(255,150,0,0.9), 1px 1px 2px rgba(0,0,0,0.8)'
          : '1px 1px 2px rgba(0,0,0,0.8)',
        border: item.self ? '1px solid rgba(255,150,0,0.9)' : 'none'
      };
    },

    fixedStyle(item) {
      const size = Math.round(item.fontsize * this.scale);
      const track = item.fixedTrack || 0;
      const offset = 8 + track * (size + 8);
      const style = {
        left: '50%',
        color: item.color,
        fontSize: size + 'px',
        textShadow: '0 1px 3px rgba(0,0,0,0.95), 0 0 4px rgba(0,0,0,0.6)',
        transform: 'translateX(-50%)',
        animation: 'dm-fade 4s linear forwards',
        background: item.self ? 'rgba(255, 150, 0, 0.45)' : 'rgba(0, 0, 0, 0.6)',
        padding: '3px 14px',
        borderRadius: '16px',
        border: item.self ? '1px solid rgba(255, 200, 80, 0.95)' : '1px solid rgba(255, 220, 100, 0.4)',
        boxShadow: item.self
          ? '0 0 8px rgba(255, 180, 0, 0.8), 0 1px 3px rgba(0,0,0,0.5)'
          : '0 1px 3px rgba(0,0,0,0.5)'
      };
      if (item.position === 'top') {
        style.top = offset + 'px';
        style.bottom = 'auto';
      } else if (item.position === 'bottom') {
        style.bottom = offset + 'px';
        style.top = 'auto';
      }
      return style;
    }
  }
};
</script>

<style scoped>
.danmaku-layer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 40px;
  overflow: hidden;
  pointer-events: none;
  z-index: 5;
  transition: opacity 0.3s;
}

.dm {
  position: absolute;
  white-space: nowrap;
  font-weight: 600;
  line-height: 1.2;
  padding: 1px 6px;
  border-radius: 3px;
  will-change: transform;
}

.dm-scroll {
  left: 100%;
  animation-name: dm-move;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
}

@keyframes dm-move {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(calc(-1 * var(--dm-width, 800px) - 100%));
  }
}

.dm-fixed {
  /* 固定弹幕的位置/样式由 fixedStyle 内联控制，这里仅保留共用样式 */
  white-space: nowrap;
}

.dm-top {
  /* top 由内联样式控制 */
}

.dm-bottom {
  /* bottom 由内联样式控制 */
}

@keyframes dm-fade {
  0% { opacity: 0; transform: translateX(-50%) translateY(-6px); }
  10% { opacity: 1; transform: translateX(-50%) translateY(0); }
  85% { opacity: 1; transform: translateX(-50%) translateY(0); }
  100% { opacity: 0; transform: translateX(-50%) translateY(6px); }
}

.dm-noble {
  display: inline-block;
  color: #fff;
  font-size: 10px;
  font-weight: bold;
  font-style: italic;
  line-height: 14px;
  padding: 0 4px;
  border-radius: 3px;
  margin-right: 4px;
  vertical-align: middle;
  text-shadow: none;
  background: linear-gradient(135deg, #ffb400, #ff6b00);
}
.dm-noble-1 { background: linear-gradient(135deg, #cd7f32, #b87333); }
.dm-noble-2 { background: linear-gradient(135deg, #c0c0c0, #a0a0a0); }
.dm-noble-3 { background: linear-gradient(135deg, #ffd700, #daa520); color: #5c3d00; }
.dm-noble-4 { background: linear-gradient(135deg, #b44cff, #7b2cbf); }
.dm-noble-5 { background: linear-gradient(135deg, #ff2d55, #c2185b); }
.dm-noble-6 { background: linear-gradient(135deg, #2a2a2a, #000000); border: 1px solid #ffd700; }
</style>
